import React, { useEffect, useState } from 'react';
import { enforceThreeDecimalFormat, enforceMoneyFormat, enforceNumericFormat } from '../../utils/formUtils';

const PURCHASE_OF_LOAN_MAP = {
    "Purchase": "Purchase",
    "Refi": "NoCash-Out Refinance",
    "Cash-Out": "Cash-Out Refinance"
};

const PROPERTY_TYPE_MAP = {
    SFR: "Detached",
    Condo: "Condominium",
    PUD: "PUD",
    Units: "Attached"
};

const AMORTIZATION_TYPE_MAP = {
    "Fixed Rate": "Fixed",
    "ARM": "AdjustableRate"
};

const FinancingRequest = ({ fieldValues, selectedLoanOption, setSelectedLoanOption }) => {

    const [selectedPropertyType, setSelectedPropertyType] = useState("");
    const [selectedAmortization, setSelectedAmortization] = useState("");
    const [armType, setArmType] = useState("");
    const [propertyUse, setPropertyUse] = useState("");
    const [purchasePrice, setPurchasePrice] = useState("");

    const isPurchaseLoan = selectedLoanOption === "Purchase";

    useEffect(() => {
        if (fieldValues["608"]) {
            setSelectedAmortization(fieldValues["608"]);
        }
        if (fieldValues["995"]) {
            setArmType(fieldValues["995"]);
        }
    }, [fieldValues]);


    useEffect(() => {
        if (fieldValues["1041"]) {
            setSelectedPropertyType(fieldValues["1041"]);
        }
    }, [fieldValues]);


    // Sync state with fetched value
    useEffect(() => {
        setSelectedLoanOption((fieldValues["19"] || "").toString().trim());
    }, [fieldValues["19"]]);

    useEffect(() => {
        if (selectedLoanOption === "Purchase") {
            setPurchasePrice(fieldValues["136"] || "");
        } else {
            setPurchasePrice("");
        }
    }, [selectedLoanOption, fieldValues["136"]]);

    useEffect(() => {
        if (fieldValues["1811"]) {
            setPropertyUse(fieldValues["1811"]);
        }
        else {
            setPropertyUse("");
        }
    }, [fieldValues]);


    const handleCheckboxSelect = (e) => {
        const { value } = e.target;

        setSelectedLoanOption((prev) => {
            const newValue = prev === value ? "" : value;

            // If not Purchase, clear Purchase Price
            if (newValue !== "Purchase") {
                setPurchasePrice("");
            }

            return newValue;
        });
    };

    const handlePropertyTypeSelect = (e) => {
        const { value } = e.target;
        setSelectedPropertyType((prev) => (prev === value ? "" : value));
    };

    const handleAmortizationSelect = (e) => {
        const { value } = e.target;
        setSelectedAmortization((prev) => {
            const newValue = prev === value ? "" : value;
            if (newValue !== "AdjustableRate") {
                setArmType("");
            }
            return newValue;
        });
    };

    const handlePropertyUseSelect = (e) => {
        const { checked, value } = e.target;
        setPropertyUse(checked ? value : "");
    };

    useEffect(() => {
        // when 356 is loaded/changed, format it
        const el = document.getElementById("field-356");
        if (!el) return;

        const raw = fieldValues["356"];
        if (raw === null || raw === undefined || raw === "") {
            el.value = "";
            return;
        }

        // set raw value first, then run your formatter
        el.value = raw.toString();
        enforceMoneyFormat({ target: el });
    }, [fieldValues["356"]]);

    return (
        <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9] mb-3">
            <div className="section-header">1. Financing Request</div>

            {/* Purchase of Loan and Property Type Grid */}
            <div className="grid md:grid-cols-2 gap-6 p-4">
                {/* Purchase of Loan */}
                <div>
                    <label className="form-label">Purchase of Loan</label>
                    <div className="space-y-2">
                        {Object.entries(PURCHASE_OF_LOAN_MAP).map(([uiLabel, encompassValue]) => (
                            <label key={uiLabel} className="checkbox-label">
                                <input
                                    type="checkbox"
                                    className="checkbox-accent"
                                    name="purchase-of-loan"
                                    id="field-19"
                                    value={encompassValue}
                                    checked={selectedLoanOption === encompassValue}
                                    onChange={handleCheckboxSelect}
                                />
                                {uiLabel === "Cash-Out"
                                    ? "Cash-Out (Allowed for Business use only)"
                                    : uiLabel}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Property Type and Use */}
                <div>
                    <label className="form-label">Property will be:</label>
                    <label className="checkbox-label mb-3">
                        <input
                            type="checkbox"
                            className="checkbox-accent"
                            id="field-1811"
                            name="property-use"
                            value="Investor"
                            onChange={handlePropertyUseSelect}
                            checked={propertyUse === "Investor"} />
                        Investment
                    </label>

                    <label className="form-label">Property Type:<span className="text-red-600 ml-1">*</span></label>
                    <div className="grid grid-cols-2 gap-2">
                        {Object.entries(PROPERTY_TYPE_MAP).map(([uiLabel, encompassValue]) => (
                            <label key={uiLabel} className="checkbox-label">
                                <input
                                    type="checkbox"
                                    className="checkbox-accent"
                                    id="field-1041"
                                    name="property-type"
                                    value={encompassValue}
                                    checked={selectedPropertyType === encompassValue}
                                    onChange={handlePropertyTypeSelect}
                                />
                                {uiLabel}
                            </label>
                        ))}
                    </div>
                </div>
            </div>

            {/* Interest Rate and Amortization */}
            <div className="grid md:grid-cols-2 gap-6 px-4 pb-4">
                <div>
                    <label className="form-label mb-1">Interest Rate:</label>
                    <input
                        type="text"
                        className="form-input-3-4"
                        id="field-3"
                        defaultValue={fieldValues["3"] || ""}
                        onInput={enforceThreeDecimalFormat}
                    />
                </div>
                <div>
                    <label className="form-label mb-1">Amortization Type:</label>
                    <div className="flex items-center gap-4">
                        {Object.entries(AMORTIZATION_TYPE_MAP).map(([uiLabel, encompassValue]) => (
                            <label key={uiLabel} className="checkbox-label">
                                <input
                                    type="checkbox"
                                    className="checkbox-accent"
                                    id="field-608"
                                    name="amortization-type"
                                    value={encompassValue}
                                    checked={selectedAmortization === encompassValue}
                                    onChange={handleAmortizationSelect}
                                />
                                {uiLabel === "ARM" ? "ARM (type):" : uiLabel}
                            </label>
                        ))}

                        {/* ARM Type textbox if ARM is selected */}
                        {selectedAmortization === "AdjustableRate" && (
                            <input
                                type="text"
                                id="field-995"
                                className="form-input-small"
                                value={armType}
                                onChange={(e) => setArmType(e.target.value)}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Amounts */}
            <div className="grid md:grid-cols-3 gap-6 px-4 pb-4">
                <div key="2">
                    <label className="form-label mb-1">Loan Amount:</label>
                    <div className="input-with-symbol">
                        <span className="mr-2">$</span>
                        <input
                            type="text"
                            className="form-input"
                            id={`field-1109`}
                            defaultValue={fieldValues["1109"] || ""}
                            onBlur={enforceMoneyFormat}
                        />
                    </div>
                </div>

                <div key="356">
                    <label className="form-label mb-1">Appraised Value:</label>
                    <div className="input-with-symbol">
                        <span className="mr-2">$</span>
                        <input
                            type="text"
                            className="form-input"
                            id="field-356"
                            defaultValue={fieldValues["356"] || ""}
                            onBlur={enforceMoneyFormat}
                        />

                    </div>
                </div>

                <div key="136">
                    <label className="form-label mb-1">Purchase Price:</label>
                    <div className="input-with-symbol">
                        <span className="mr-2">$</span>
                        <input
                            type="text"
                            className={`form-input ${!isPurchaseLoan ? "cursor-not-allowed bg-gray-100" : ""}`}
                            id="field-136"
                            value={purchasePrice}
                            readOnly={!isPurchaseLoan}
                            onChange={(e) => {
                                if (!isPurchaseLoan) return;
                                setPurchasePrice(e.target.value);
                            }}
                            onBlur={(e) => {
                                if (!isPurchaseLoan) return;
                                enforceMoneyFormat(e);
                                setPurchasePrice(e.target.value);
                            }}
                        />
                    </div>
                </div>
            </div>

        </div>
    );
};

export default FinancingRequest;
